/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright (c) 2017 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains the property of
 * Pearson Education, Inc. The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S.
 * and Foreign Patents, patent applications, and are protected by trade secret
 * or copyright law. Dissemination of this information, reproduction of this
 * material, and copying or distribution of this software is strictly forbidden
 * unless prior written permission is obtained from Pearson Education, Inc.
 */

import React from "react";
import PropTypes from "prop-types";

import Icon from "../Icon";
import styles from "./searchSuggestions.module.scss";
import { generateId } from "../_util/common.js";

class SearchSuggestions extends React.Component {
  static propTypes = {
    histories: PropTypes.array,
    suggestions: PropTypes.array,
    focusedIndex: PropTypes.number,
    onClick: PropTypes.func,
    onKeyDown: PropTypes.func,
    id: PropTypes.string,
    placeholder: PropTypes.string,
  };

  static defaultProps = {
    histories: [],
    suggestions: [],
    focusedIndex: -1,
  };

  constructor(props) {
    super(props);
    this.items = [];
  }

  componentDidUpdate(prevProps) {
    if (this.props.focusedIndex !== prevProps.focusedIndex) {
      if (this.items[this.props.focusedIndex]) {
        this.items[this.props.focusedIndex].focus();
      }
    }
  }

  generateItemText = (history) => {
    let response;
    history.replace(
      new RegExp(
        this.props.searchText.replace(/[.*+?^${}()|[\]\\]/g, "\\$&"),
        "gi"
      ),
      (match, offset, string) => {
        response = { match, offset, string };
      }
    );
    if (response && response.match) {
      return (
        <>
          <span>{response.string.slice(0, response.offset)}</span>
          <b>{response.match}</b>
          <span>
            {response.string.slice(
              response.offset + response.match.length,
              response.string.length
            )}
          </span>
        </>
      );
    } else {
      return history;
    }
  };

  setRef = (ref) => {
    this.items.push(ref);
  };

  calcIndex = (mainIndex, subIndex) => {
    let suffix = this.props.suggestions.map((a, index) => {
      if (index < mainIndex) {
        return a.items.length;
      } else {
        return 0;
      }
    });

    suffix = suffix.reduce((a, b) => a + b, 0);
    const total = this.props.histories.length + suffix + subIndex;
    return total;
  };

  render() {
    const {
      histories,
      suggestions,
      onClick,
      searchText,
      placeholder,
    } = this.props;

    return (
      <ul
        className={styles.suggestions_container}
        role="listbox"
        aria-label={placeholder}
        id={`${this.props.id}-popup`}
      >
        {histories.length > 0 && (
          <li className="recent" role="option" aria-selected={false}>
            <div className={styles.suggestions_title}>Recent searches</div>
            <ul
              className={styles.suggestions_results}
              role="listbox"
              aria-label="Recent searches"
            >
              {histories.map((history, index) => {
                return (
                  <li
                    id={`historiesItem${index}`}
                    role="option"
                    aria-live="polite"
                    key={`search-history-${index}`}
                    className={styles.suggestions_results_item}
                    onClick={() => {
                      onClick(history, index);
                    }}
                    onKeyDown={(e) => this.props.onKeyDown(e, history, index)}
                    ref={this.setRef}
                    tabIndex="-1"
                    aria-selected={searchText === history}
                  >
                    <Icon
                      className={styles.suggestions_results_item_icon}
                      name={`${generateId()}-clock-18`}
                      isHidden={true}
                      fileName="clock-18"
                      title=""
                    />
                    {this.generateItemText(history)}
                  </li>
                );
              })}
            </ul>
          </li>
        )}
        {suggestions.map((suggestion, index) => {
          return (
            <li role="option" key={`search-suggestion-${index}`} aria-selected={false}> 
              <hr className={styles.suggestions_results_hr}></hr>
              <div className={styles.suggestions_title}>{suggestion.title}</div>
              <ul
                className={styles.suggestions_results}
                aria-label={suggestion.title}
                role="listbox"
              >
                {suggestion.items &&
                  suggestion.items.map((item, i) => {
                    return (
                      <li
                        id={`sugesstionsItem${this.calcIndex(index, i)}`}
                        className={styles.suggestions_results_item}
                        role="option"
                        key={`suggestion-${index}-item-${i}`}
                        onClick={() => onClick(item, this.calcIndex(index, i))}
                        onKeyDown={(e) =>
                          this.props.onKeyDown(
                            e,
                            item,
                            this.calcIndex(index, i)
                          )
                        }
                        ref={this.setRef}
                        aria-label={`${item}, ${suggestion.title}`}
                        tabIndex="-1"
                        aria-selected={searchText === item}
                      >
                        {this.generateItemText(item)}
                      </li>
                    );
                  })}
              </ul>
            </li>
          );
        })}
      </ul>
    );
  }
}

export default SearchSuggestions;
